// Layer 3 (Transformations): shared series projections for the five glass
// landings — Paul's region composition, the seed series' values (decisions.md
// § recovery basis, 17 Sep 15:50).

import type { SessionState } from '@/versions/v1/schema/scenario';
import { formatCurrency } from '@/versions/v1/shared/money';
import { smelterPenaltyLimit } from '@/versions/v1/shared/bunker-hill-smelter-terms';
import type { SeriesTime } from '@/versions/v1/schema/reading-series';
import { productionDayOf } from '@/versions/v1/shared/clock.derive';
// productionDayOf also used by metProcessVarsChart daily alignment
import { readingByIdLookup } from '@/versions/v1/shared/reading.derive';
import { formatPtDelta, recoveryPtDelta } from '@/versions/v1/shared/recovery-vocab.derive';
import { CONTROL_PLAN_ZN_ROWS } from '@/versions/v1/shared/bunker-hill-control-plan-seed';
import { readingsAt, seriesAt, trendBundle, type TrendBundle } from '@/versions/v1/shared/series.derive';
import type { VizTone } from '@/versions/v1/modules/_viz';
import {
  dailyTrendSlice,
  formatCompositeLabel,
  lastCompositeStamps,
  shiftValueAt,
} from './LandingCursor.transform';

/** The locked comp's round axis grids (`.impeccable/mocks/decision/landing-control-met.html`
 *  A): the window it draws plus the ticks it draws inside it. The comp's pH
 *  step (0.3) and P80 step (4) are not `niceStep` decades, so the grid is
 *  stated rather than derived; `TrendChart`'s `niceStep` still governs every
 *  axis that passes no `*YTicks` (issue #755). */
const MET_RECOVERY_AXIS = { domain: [81.5, 89], ticks: [82, 84, 86, 88] } as const;
const MET_PH_AXIS = { domain: [10.1, 11.2], ticks: [10.2, 10.5, 10.8, 11.1] } as const;
const MET_P80_AXIS = { domain: [72, 84], ticks: [72, 76, 80, 84] } as const;
const LIMS_LEFT_AXIS = { domain: [52, 72], ticks: [52, 57, 62, 67, 72] } as const;
const LIMS_RIGHT_AXIS = { domain: [1.5, 3.5], ticks: [1.5, 2.0, 2.5, 3.0, 3.5] } as const;

export function p80DesignMicrons(state: SessionState): number {
  return readingValue('rd-spec-p80', state).design ?? 74;
}

const ZN_REC_ID = 'rd-kpi-zn-rec';
const PB_REC_ID = 'rd-kpi-pb-rec';
const ORE_TD_ID = 'rd-kpi-ore-td';

/** Fresh feed at the cursor in stph: the measured `rd-kpi-ore-td` over 24 h,
 *  the one rate every landing's feed figure reads (71.7 stph at the demo
 *  cursor). The feed-hold issue and the P80 prose state this figure, never a
 *  typed rate (#733). */
export function feedStphAt(state: SessionState): number {
  const feed = readingValue(ORE_TD_ID, state);
  if (feed.value == null) throw new Error('Landing series: missing rd-kpi-ore-td at cursor');
  return feed.value / 24;
}

/** The fresh-feed design rate in stph (`rd-kpi-ore-td` design over 24 h, 75.0). */
export function feedDesignStph(state: SessionState): number {
  const design = readingValue(ORE_TD_ID, state).design;
  if (design == null) throw new Error('Landing series: missing rd-kpi-ore-td design');
  return design / 24;
}

function formatNumber(n: number, decimals = 1): string {
  return n.toFixed(decimals);
}

function readingValue(id: string, state: SessionState) {
  const projected = readingsAt(state).find((r) => r.id === id);
  const seed = readingByIdLookup(id);
  if (!seed) throw new Error(`Missing reading ${id}`);
  return projected ?? seed;
}

function recoveryActual(id: string, state: SessionState): number {
  return readingValue(id, state).value ?? 0;
}

function recoveryDesign(id: string, state: SessionState): number {
  return readingValue(id, state).design ?? 0;
}

export function mtdDaysElapsed(cursor: SeriesTime): number {
  const day = parseInt(productionDayOf(cursor).date.split('-')[2]!, 10);
  return Math.max(1, day);
}

export function mtdImpactTotal(impactPerDay: number, state: SessionState): number {
  return impactPerDay * mtdDaysElapsed(state.cursor);
}

export function formatMtdImpact(impactPerDay: number, state: SessionState): string {
  const total = mtdImpactTotal(impactPerDay, state);
  return formatCurrency(total);
}

export function znGapPpt(state: SessionState): number {
  const actual = recoveryActual(ZN_REC_ID, state);
  const design = recoveryDesign(ZN_REC_ID, state);
  return Math.abs(recoveryPtDelta(actual, design));
}

export function formatPptGap(state: SessionState): string {
  return `${znGapPpt(state).toFixed(1)} pt`;
}

/** The Control metallurgist's four process KPIs (polish.P26 #713, locked comp
 *  `landing-control-met.html` A): Zn recovery leads the strip, the other three
 *  are the Process KPI trends figure row. Grammar per the cross-route rulings:
 *  tonnes lead `vs budget` with design beside it; recoveries lead the signed
 *  `pt` gap to design, then Budget, then the 7-day direction; the Zn float pH
 *  reads the Control Plan's `cp-zn-ph` band (adjudication 9), never a typed
 *  target. */
export type MetKpiTile = {
  id: 'cmet-kpi-tonnes' | 'cmet-kpi-pb-rec' | 'cmet-kpi-zn-rec' | 'cmet-kpi-zn-ph';
  label: string;
  value: string;
  /** The lead comparison, set in 600 ink-secondary (comp `.cmp b`). */
  captionLead: string;
  caption: string;
  tone: VizTone;
  provenance?: string;
  provenanceChip?: string;
};

function sevenDayDirection(delta: number): string {
  if (delta < -0.05) return 'falling';
  if (delta > 0.05) return 'rising';
  return 'stable';
}

function recoveryTile(
  id: 'cmet-kpi-pb-rec' | 'cmet-kpi-zn-rec',
  metal: 'Pb' | 'Zn',
  readingId: string,
  seriesId: string,
  state: SessionState,
  tone: VizTone,
): MetKpiTile {
  const reading = readingValue(readingId, state);
  const actual = reading.value ?? 0;
  const design = reading.design ?? 0;
  const budget = reading.target;
  const week = dailySevenDaySeriesDelta(seriesId, state);
  const budgetPart = budget !== undefined ? ` · Budget ${formatNumber(budget, 1)}%` : '';
  return {
    id,
    label: `${metal} recovery · 24-h average`,
    value: `${formatNumber(actual, 1)}%`,
    captionLead: `${formatPtDelta(recoveryPtDelta(actual, design))} vs ${formatNumber(design, 1)}% design`,
    caption: `${budgetPart} · ${sevenDayDirection(week.delta)} ${formatPtDelta(week.delta)} over 7 days`,
    tone,
    provenanceChip: 'derived',
    provenance: `${readingId} at session cursor`,
  };
}

export function metallurgistKpiRow(state: SessionState): MetKpiTile[] {
  const pbActual = recoveryActual(PB_REC_ID, state);
  const pbDesign = recoveryDesign(PB_REC_ID, state);
  const ore = readingValue(ORE_TD_ID, state);
  const oreBudget = ore.target ?? 1440;
  const oreValue = ore.value ?? 0;
  const oreDesign = ore.design;
  const phView = seriesAt('rs-zn-ph', state);
  const phActual = phView.latest?.value ?? 10.4;
  const phBand = CONTROL_PLAN_ZN_ROWS.find((row) => row.id === 'cp-zn-ph')!.specOverlay;
  const phOutside = (phBand.lo !== undefined && phActual < phBand.lo) || (phBand.hi !== undefined && phActual > phBand.hi);
  const oreDelta = oreValue - oreBudget;
  const orePct = (oreDelta / oreBudget) * 100;
  // Harder ore lowers throughput, so it explains a tonnage shortfall and
  // nothing else — the attribution is appended only when the tile reads
  // below budget, never beside an above-budget value.
  const oreCause = oreDelta < 0 ? ' · ore hardness' : '';
  const znGap = znGapPpt(state);
  const pbGap = Math.abs(recoveryPtDelta(pbActual, pbDesign));

  return [
    {
      id: 'cmet-kpi-tonnes',
      label: 'Tonnes · 24 h',
      value: `${Math.round(oreValue).toLocaleString()} t/d`,
      captionLead: `${orePct >= 0 ? '+' : '−'}${Math.abs(orePct).toFixed(1)}% vs budget`,
      caption: ` ${Math.round(oreBudget).toLocaleString()} t/d${oreDesign !== undefined ? ` · design ${Math.round(oreDesign).toLocaleString()} t/d` : ''}${oreCause}`,
      tone: oreValue < oreBudget ? 'bad' : 'ok',
      provenanceChip: 'derived',
      provenance: 'rd-kpi-ore-td at session cursor',
    },
    // One gap rule for both recovery tiles (polish.P26 r1): Pb 3.1 pt below
    // design read milder than Zn at 2.8 pt, and the comp draws Pb `bad`.
    recoveryTile('cmet-kpi-pb-rec', 'Pb', PB_REC_ID, 'rs-pb-rec', state, pbGap > 2 ? 'bad' : pbGap > 0.5 ? 'warn' : 'ok'),
    recoveryTile('cmet-kpi-zn-rec', 'Zn', ZN_REC_ID, 'rs-zn-rec', state, znGap > 2 ? 'bad' : 'warn'),
    {
      id: 'cmet-kpi-zn-ph',
      label: `Zn float pH · ${readingByIdLookup('rd-kpv-zn-ph')?.tag ?? 'AT-150'}`,
      value: formatNumber(phActual, 1),
      captionLead: `vs ${formatNumber(phBand.lo ?? 0, 1)}–${formatNumber(phBand.hi ?? 0, 1)} band`,
      caption: ' · calibration in progress',
      tone: phOutside ? 'warn' : 'ok',
      provenanceChip: 'derived',
      provenance: 'rs-zn-ph latest at session cursor · band cp-zn-ph',
    },
  ];
}

export type LandingTrendBundle = Omit<TrendBundle, 'series' | 'references'> & {
  series: readonly {
    name: string;
    points: readonly number[];
    tone: 'warn' | 'info' | 'ok';
    axis?: 'left' | 'right';
  }[];
  references: readonly {
    value: number;
    label: string;
    tone: 'warn' | 'info' | 'ok' | 'neutral';
    axis?: 'left' | 'right';
  }[];
  leftYDomain?: readonly [number, number];
  rightYDomain?: readonly [number, number];
  /** The comp's explicit round tick grid for an axis (`TrendChart`'s opt-in
   *  `leftYTicks` / `rightYTicks`). Absent, `TrendChart` derives the axis. */
  leftYTicks?: readonly number[];
  rightYTicks?: readonly number[];
};

export function metRecoveryTrendChart(state: SessionState): LandingTrendBundle {
  const bundle = trendBundle(['rs-pb-rec', 'rs-zn-rec'], state, { points: 7 });
  const pbDesign = recoveryDesign(PB_REC_ID, state);
  const znDesign = recoveryDesign(ZN_REC_ID, state);
  return {
    ...bundle,
    series: bundle.series.map((s) => ({
      ...s,
      name: s.name === 'Pb' ? 'Pb rec %' : 'Zn rec %',
    })),
    references: [
      { value: pbDesign, label: `Pb design ${pbDesign.toFixed(1)}%`, tone: 'info' },
      { value: znDesign, label: `Zn design ${znDesign.toFixed(1)}%`, tone: 'warn' },
    ],
    // The comp's 82–88 grid. Its window still holds the Pb design 88.2 line,
    // so the axis reads 82/84/86/88 rather than the auto 82–90 (#755).
    leftYDomain: MET_RECOVERY_AXIS.domain,
    leftYTicks: MET_RECOVERY_AXIS.ticks,
  };
}

export function metProcessVarsChart(state: SessionState): LandingTrendBundle {
  const phDaily = dailyTrendSlice('rs-zn-ph', state, 7);
  const p80Daily = dailyTrendSlice('rs-sa01-p80', state, 7);
  const p80ByStamp = new Map(p80Daily.stamps.map((at, i) => [at.split('T')[0], p80Daily.points[i]!]));
  const p80Points = phDaily.stamps.map((at) => {
    const day = productionDayOf(at).date;
    const match = p80Daily.stamps.find((s) => productionDayOf(s).date === day);
    return match ? (p80ByStamp.get(match.split('T')[0]!) ?? 0) : 0;
  });
  const p80Design = p80DesignMicrons(state);
  return {
    categories: phDaily.categories,
    series: [
      { name: 'pH', points: phDaily.points, tone: 'warn', axis: 'left' },
      { name: 'P80 (µm)', points: p80Points, tone: 'info', axis: 'right' },
    ],
    references: [
      { value: 11.0, label: 'pH target 11.0', tone: 'warn', axis: 'left' },
      { value: p80Design, label: `P80 design ${p80Design} µm`, tone: 'info', axis: 'right' },
    ],
    leftYDomain: MET_PH_AXIS.domain,
    leftYTicks: MET_PH_AXIS.ticks,
    rightYDomain: MET_P80_AXIS.domain,
    rightYTicks: MET_P80_AXIS.ticks,
  };
}

export function seniorMetTrendChart(state: SessionState): LandingTrendBundle {
  const bundle = trendBundle(['rs-zn-rec', 'rs-pb-rec', 'rs-mill-util'], state, { points: 7 });
  const znDesign = recoveryDesign(ZN_REC_ID, state);
  const millUtilTarget = readingByIdLookup('rd-kpi-mill-util')?.target ?? 92;
  const seriesNames = ['Zn rec %', 'Pb rec %', 'Mill util % (right axis)'] as const;
  const seriesTones = ['warn', 'info', 'warn'] as const;
  const seriesAxes = ['left', 'left', 'right'] as const;
  return {
    categories: bundle.categories,
    series: bundle.series.map((s, index) => ({
      name: seriesNames[index] ?? s.name,
      points: s.points,
      tone: seriesTones[index] ?? s.tone,
      axis: seriesAxes[index] ?? 'left',
    })),
    references: [
      { value: znDesign, label: `Zn design ${znDesign.toFixed(1)}%`, tone: 'warn', axis: 'left' },
      { value: millUtilTarget, label: `Mill util target ${millUtilTarget.toFixed(1)}%`, tone: 'info', axis: 'right' },
    ],
    leftYDomain: [80, 86],
    rightYDomain: [88, 94],
  };
}

export function limsAssayTrendChart(state: SessionState): LandingTrendBundle {
  const pbInZnLimit = smelterPenaltyLimit('Zn', 'Pb');
  // One cadence for the chart and the register below it (#755): the last six
  // 12 h composites the register lists, oldest first — the same six rows the
  // locked comp plots. `dailyTrendSlice`'s one-per-production-day sampling
  // stays with the 7-day figure rows and the cross-area table, which keep
  // their own 7-day window.
  const stamps = [...lastCompositeStamps(state, 6)].reverse();
  const categories = stamps.map((at) => formatCompositeLabel(at, state.cursor, false));
  const seriesAtStamp = (seriesId: string, stamp: SeriesTime): number =>
    shiftValueAt(seriesId, stamp, state) ?? 0;
  return {
    categories,
    series: [
      { name: 'Pb grade %', points: stamps.map((s) => seriesAtStamp('rs-pb-grade', s)), tone: 'info', axis: 'left' },
      { name: 'Zn grade %', points: stamps.map((s) => seriesAtStamp('rs-zn-grade', s)), tone: 'warn', axis: 'left' },
      { name: 'Pb in Zn con %', points: stamps.map((s) => seriesAtStamp('rs-spec-zn-pb', s)), tone: 'warn', axis: 'right' },
    ],
    references: [
      { value: 62, label: 'Pb grade min 62%', tone: 'info', axis: 'left' },
      { value: 55, label: 'Zn grade min 55%', tone: 'warn', axis: 'left' },
      { value: pbInZnLimit, label: `Pb in Zn limit ${pbInZnLimit.toFixed(1)}%`, tone: 'warn', axis: 'right' },
    ],
    leftYDomain: LIMS_LEFT_AXIS.domain,
    leftYTicks: LIMS_LEFT_AXIS.ticks,
    rightYDomain: LIMS_RIGHT_AXIS.domain,
    rightYTicks: LIMS_RIGHT_AXIS.ticks,
  };
}

/** Seven elapsed days on the series cadence — hourly 168, shift 14, daily 7. */
export function sevenDayPointCount(cadence: 'hourly' | 'shift' | 'daily'): number {
  if (cadence === 'hourly') return 168;
  if (cadence === 'shift') return 14;
  return 7;
}

export function sevenDaySeriesDelta(
  seriesId: string,
  state: SessionState,
): { first: number; last: number; delta: number } {
  const view = seriesAt(seriesId, state);
  const count = sevenDayPointCount(view.cadence);
  const window = view.points.slice(-count);
  const points = window.filter(
    (p): p is { at: SeriesTime; value: number } => p.value !== null,
  );
  if (points.length < 2) return { first: 0, last: 0, delta: 0 };
  const first = points[0]!.value;
  const last = points[points.length - 1]!.value;
  return { first, last, delta: last - first };
}

/**
 * Seven-day delta from one sample per production day — the same sampling the
 * trend charts plot (`dailyTrendSlice`). A shift- or hourly-cadence series
 * read with `sevenDaySeriesDelta` starts on a part-day stamp, so its `first`
 * disagrees with the chart above it; any row that sits beside a daily chart
 * must use this instead.
 */
export function dailySevenDaySeriesDelta(
  seriesId: string,
  state: SessionState,
): { first: number; last: number; delta: number } {
  const slice = dailyTrendSlice(seriesId, state, 7);
  if (slice.points.length < 2) return { first: 0, last: 0, delta: 0 };
  const first = slice.points[0]!;
  const last = slice.points[slice.points.length - 1]!;
  return { first, last, delta: last - first };
}
